import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import {action, makeAutoObservable, observable, reaction} from "mobx";
import {RequestState} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {IUser, IUserStore} from "data/stores/user/user.store";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalsStore} from "data/stores/modals/modals.store";
import type {IPlayersStore} from "data/stores/players/players.store";
import type {IRoundsStore, ISession} from "data/stores/rounds/rounds.store";
import type {ITeamStore} from "data/stores/team/team.store";
import type {IRankingsStore} from "data/stores/rankings/rankings.store";
import type {IUserTeamResponse} from "data/providers/api/team.api.provider";

interface IProps {
	roundId: number;
	userId: number;
}

export interface IUserTeamController extends ViewController<IProps> {
	isLoading: boolean;

	get i18n(): ILocalizationStore;
	get user(): IUser | undefined;
	get userTeam(): IUserTeamResponse;
	get lastCompleteOrPlayingSession(): ISession | undefined;
}

@injectable()
export class UserTeamController implements IUserTeamController {
	@observable isLoading: boolean = false;
	@observable _requestState: RequestState = RequestState.IDLE;
	@observable _roundId: number = 0;
	@observable _userId: number = 0;
	private _disposerReaction!: ReturnType<typeof reaction>;

	get i18n() {
		return this._i18nStore;
	}

	get user() {
		return this._userStore.user;
	}

	get userTeam() {
		return this._teamStore.userTeam;
	}

	get lastCompleteOrPlayingSession() {
		return this._roundStore.lastCompleteOrPlayingSession;
	}

	constructor(
		@inject(Bindings.UserStore) private _userStore: IUserStore,
		@inject(Bindings.LocalizationStore) private _i18nStore: ILocalizationStore,
		@inject(Bindings.ModalsStore) private _modalStore: IModalsStore,
		@inject(Bindings.PlayersStore) private _playerStore: IPlayersStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.TeamStore) private _teamStore: ITeamStore,
		@inject(Bindings.RankingsStore) private _rankingStore: IRankingsStore
	) {
		makeAutoObservable(this);
	}

	setIsLoading(value: boolean): void {
		this.isLoading = value;
	}

	@action onChange({roundId, userId}: IProps) {
		this._roundId = roundId;
		this._userId = userId;
	}

	dispose() {
		this._disposerReaction?.();
	}

	async init(param: IProps) {
		this.onChange(param);
		this.setIsLoading(true);

		await this._roundStore.fetchRounds();
		await this._playerStore.fetchPlayers();

		if (this._roundStore.lastCompleteOrPlayingSession?.id) {
			this._roundStore.setUserTeamSelectedRoundId(
				this._roundStore.lastCompleteOrPlayingSession.gamedayId
			);
			this._roundStore.setUserTeamSelectedRoundSessions();
			this._roundStore.setUserTeamSelectedSessionId(
				this._roundStore.lastCompleteOrPlayingSession.id
			);
		}

		// if (this._roundStore.userTeamSelectedRoundId) {
		// 	await this._rankingStore.fetchGamebar({
		// 		gamedayId: this._roundStore.userTeamSelectedRoundId,
		// 	});
		// }

		// if (this._roundStore.selectedSessionId) {
		// 	await this._teamStore.getTeam({
		// 		roundId: this._roundStore.selectedSessionId,
		// 	});
		// }

		await this._teamStore.getUserTeam({
			roundId: this._roundId,
			userId: this._userId,
		});

		this._disposerReaction = reaction(
			() => this._roundStore.userTeamsSelectedSessionId,
			(sessionId) => {
				void this._teamStore.getUserTeam({
					roundId: sessionId,
					userId: this._userId,
				});
			},
			{fireImmediately: false}
		);

		this.setIsLoading(false);
	}
}
